import { Link } from 'react-router-dom';
import { FaInfoCircle, FaCopy, FaHistory } from 'react-icons/fa';
import NewTooltip from '../../../atoms/NewTooltip';
import { CreditAnalysis } from '../types';
import DataTableColumn from '../components/DataTableColumn';
import { getRatingColor, fmtCurrency, fmtPercent, fmtMil, fmtMilDiv, financialBg } from './analysisFormatters';
import EditableDecision from './EditableDecision';
import EditableTextField from './EditableTextField';
import EditableApprovedValue from './EditableApprovedValue';

// ═══════════════════════════════════════════════════════════════
// INTERFACES & TYPES
// ═══════════════════════════════════════════════════════════════

interface DecisionPayload {
    status: string;
    approvedValue?: number;
    justification?: string;
    creditObs?: string;
    cprNumber?: string;
    decidedBy?: string;
}

interface AnalysisColumnsProps {
    onOpenModal: (row: CreditAnalysis) => void;
    onOpenDrawee: (doc: string, itemId: string, name: string, batchId: string) => void;
    onSaveDecision: (batchId: string, uuid: string, data: DecisionPayload) => Promise<any>;
    onUpdateRow: (uuid: string, updates: Partial<CreditAnalysis>) => void;
    featureFlags: Record<string, boolean>;
    hideBatchColumn?: boolean;
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function AnalysisColumns({ onOpenModal, onOpenDrawee, onSaveDecision, onUpdateRow, featureFlags, hideBatchColumn }: AnalysisColumnsProps) {
    return (
        <>
            <DataTableColumn<CreditAnalysis> id="col-actions" field="actions" title="Ações" width="80px" canSort={false} sticky>
                {(row) => (
                    <div className="flex items-center justify-center gap-2">
                        {featureFlags['credit-analysis:approve'] !== false && (
                            <NewTooltip content="Informações de Consulta">
                                <button onClick={() => onOpenModal(row)} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                                    <FaInfoCircle className="text-lg" />
                                </button>
                            </NewTooltip>
                        )}
                        <NewTooltip content="Histórico do Sacado">
                            <a href={`/#/credit-analysis/historico?document=${encodeURIComponent(row.document)}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                                <FaHistory className="text-sm" />
                            </a>
                        </NewTooltip>
                    </div>
                )}
            </DataTableColumn>

            {!hideBatchColumn && (
                <DataTableColumn<CreditAnalysis> id="col-1" field="batchNumber" title="Lote" width="150px" canSort sticky>
                    {(row) => row.batchNumber
                        ? <Link to={`/credit-analysis/batch/${row.id}`} className="text-blue-600 dark:text-blue-400 hover:underline font-medium">{row.batchNumber}</Link>
                        : <span>-</span>}
                </DataTableColumn>
            )}

            <DataTableColumn<CreditAnalysis> id="col-2" field="drawee" title="Sacado" width="250px" canSort sticky>
                {(row) => <span className="dark:text-gray-200 block" title={row.drawee}>{row.drawee}</span>}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-15" field="rating" title="Rating" width="100px" canSort align="center" sticky filterType="multiselect"
                filterOptions={['AA+', 'AA', 'AA-', 'AX', 'A+', 'A', 'A-', 'BX', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'D-', 'EX', 'E+', 'E', 'E-', 'F', 'TBD', '-'].map(v => ({ value: v, label: v === '-' ? 'Sem Rating' : v }))}>
                {(row) => { const c = getRatingColor(row.rating); return <span className={`px-2 py-1 rounded font-medium ${c.bg} ${c.text}`}>{row.rating || '-'}</span>; }}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-21" field="originalValue" title="Vlr Original (R$)" width="150px" canSort align="right" sticky filterType="range">
                {(row) => <span className="dark:text-gray-200">{fmtCurrency(row.originalValue)}</span>}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-3" field="document" title="CPF/CNPJ" width="170px" canSort>
                {(row) => (
                    <button onClick={() => onOpenDrawee(row.document, row.uuid, row.drawee, row.id)} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline font-mono">
                        {row.document}
                    </button>
                )}
            </DataTableColumn>

            {!hideBatchColumn && <DataTableColumn<CreditAnalysis> id="col-4" field="date" title="Data" width="120px" canSort />}

            <DataTableColumn<CreditAnalysis> id="col-5" field="assignor" title="Cedente" width="250px" canSort>
                {(row) => <span className="dark:text-gray-200 block" title={row.assignor}>{row.assignor}</span>}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-6" field="type" title="Tipo" width="100px" canSort align="center" filterType="select" filterOptions={[{ value: 'PF', label: 'PF' }, { value: 'PJ', label: 'PJ' }]} />

            <DataTableColumn<CreditAnalysis> id="col-7" field="year" title="Ano" width="120px" canSort>
                {(row) => {
                    if (!row.year) return <span>-</span>;
                    const yearNum = parseInt(row.year);
                    const isPF = row.type === 'PF';
                    const age = isPF ? new Date().getFullYear() - yearNum : null;
                    const highlight = isPF ? (age != null && age > 75) : yearNum > 2024;
                    return <span className={`block px-2 py-1 rounded font-medium text-center ${highlight ? 'bg-[#ea9999] text-white' : 'dark:text-gray-200'}`}>{row.year}{isPF && age != null ? ` (${age}a)` : ''}</span>;
                }}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-8" field="city" title="Cidade" width="150px" canSort />
            <DataTableColumn<CreditAnalysis> id="col-9" field="state" title="UF" width="80px" canSort align="center" />
            <DataTableColumn<CreditAnalysis> id="col-9b" field="economicGroup" title="Grupo Econômico" width="180px" canSort />

            <DataTableColumn<CreditAnalysis> id="col-10" field="qsa" title="QSA (R$ mil)" width="120px" canSort filterType="range">
                {(row) => {
                    if (!row.qsa) return <span>-</span>;
                    const val = parseFloat(row.qsa) / 1000;
                    const cls = val < 100 ? 'bg-[#ea9999] text-white' : val <= 1000 ? 'bg-yellow-400 text-gray-900' : val > 40000 ? 'bg-green-500 text-white' : 'dark:text-gray-200';
                    return <span className={`block px-2 py-1 rounded font-medium text-right ${cls}`}>{fmtMilDiv(row.qsa)}</span>;
                }}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-13" field="annualRevenue" title="Rec. Anual (R$ mil)" width="150px" canSort align="right" filterType="range" bgClassName={financialBg}>
                {(row) => <span className="dark:text-gray-200">{fmtMil(row.annualRevenue)}</span>}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-14" field="equity" title="PL (R$ mil)" width="120px" canSort filterType="range" bgClassName={financialBg}>
                {(row) => row.equity ? <span className={`block px-2 py-1 rounded font-medium text-right ${parseFloat(row.equity) < 0 ? 'bg-[#ea9999] text-white' : 'dark:text-gray-200'}`}>{fmtMil(row.equity)}</span> : <span>-</span>}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-reasons" field="reasons" title="Não conformidades" width="250px">
                {(row) => {
                    const reasons = Array.isArray(row.reasons) ? row.reasons : [];
                    if (!reasons.length) return <span className="dark:text-gray-200">-</span>;
                    return <div className="text-xs text-gray-700 dark:text-gray-300">{reasons.map((r, i) => <div key={`${i}-${r}`} className="truncate" title={r}>{r}</div>)}</div>;
                }}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-16" field="recommendation" title="Recomendação" width="140px" canSort filterType="multiselect" filterOptions={[{ value: 'APPROVED', label: 'Aprovado' }, { value: 'REPROVADO', label: 'Reprovado' }, { value: 'PENDENTE', label: 'Pendente' }]}>
                {(row) => {
                    const r = (row.recommendation || '').toUpperCase();
                    const isApproved = r === 'APPROVED' || r === 'APROVADO';
                    const isRejected = r === 'REJECTED' || r === 'REPROVADO';
                    const isPending = r === 'PENDING' || r === 'PENDENTE';
                    const label = isApproved ? 'Aprovado' : isRejected ? 'Reprovado' : isPending ? 'Pendente' : row.recommendation || '-';
                    const cls = isApproved ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : isRejected ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : isPending ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
                    return <span className={`block px-2 py-1 rounded text-xs font-medium text-center ${cls}`}>{label}</span>;
                }}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-17" field="status" title="Decisão" width="120px" canSort filterType="multiselect" filterOptions={[{ value: 'PENDING', label: 'Pendente' }, { value: 'APPROVED', label: 'Aprovado' }, { value: 'REJECTED', label: 'Reprovado' }, { value: 'PRE_APPROVED', label: 'Pré-aprovado' }, { value: 'CANCELED', label: 'Cancelado' }]}>
                {(row) => <EditableDecision row={row} onSave={async (uuid, status) => { await onSaveDecision(row.id, uuid, { status }); onUpdateRow(uuid, { status }); }} />}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-17b" field="suggestedValue" title="Valor Sugerido (R$)" width="170px" canSort align="right">
                {(row) => (
                    <div className="flex items-center justify-end gap-1">
                        <span className="dark:text-gray-200">{fmtCurrency(row.suggestedValue)}</span>
                        {parseFloat(row.suggestedValue) > 0 && (
                            <button onClick={async () => {
                                const num = parseFloat(row.suggestedValue);
                                await onSaveDecision(row.id, row.uuid, { status: 'APPROVED', approvedValue: num });
                                const scr = row.type === 'PF' ? parseFloat(row.scrRural || '0') : parseFloat(row.scrTotal || '0');
                                const approvedPercent = scr > 0 ? String(num / scr) : '';
                                const currentExposure = parseFloat(row.currentExposure || '0');
                                const potentialExposure = (currentExposure + num) > 0 ? String(currentExposure + num) : '';
                                onUpdateRow(row.uuid, { approvedValue: String(num), status: 'APPROVED', approvedPercent, potentialExposure });
                            }} className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300" title="Aprovar com valor sugerido">
                                <FaCopy className="text-sm" />
                            </button>
                        )}
                    </div>
                )}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-18" field="approvedValue" title="Valor Aprovado (R$)" width="150px" canSort align="right">
                {(row) => <EditableApprovedValue row={row} onSave={async (uuid, num) => {
                    await onSaveDecision(row.id, uuid, { status: row.status || 'PENDING', approvedValue: num });
                    const scr = row.type === 'PF' ? parseFloat(row.scrRural || '0') : parseFloat(row.scrTotal || '0');
                    const approvedPercent = scr > 0 ? String(num / scr) : '';
                    const currentExposure = parseFloat(row.currentExposure || '0');
                    const potentialExposure = (currentExposure + num) > 0 ? String(currentExposure + num) : '';
                    onUpdateRow(uuid, { approvedValue: String(num), approvedPercent, potentialExposure });
                }} />}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-19" field="approvedPercent" title="%Aprov/SCR" width="130px" canSort align="right">
                {(row) => <span className="dark:text-gray-200">{fmtPercent(row.approvedPercent)}</span>}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-20" field="justification" title="Justificativa" width="200px" canSort={false}>
                {(row) => <EditableTextField value={row.justification} maxLength={500} onSave={async (val) => { await onSaveDecision(row.id, row.uuid, { status: row.status || 'PENDING', justification: val }); onUpdateRow(row.uuid, { justification: val }); }} />}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-22" field="dupPercent" title="% Dup/SCR" width="120px" canSort align="right">
                {(row) => <span className="dark:text-gray-200">{fmtPercent(row.dupPercent)}</span>}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-23" field="creditObs" title="Obs Crédito" width="200px" canSort={false}>
                {(row) => <EditableTextField value={row.creditObs} maxLength={500} onSave={async (val) => { await onSaveDecision(row.id, row.uuid, { status: row.status || 'PENDING', creditObs: val }); onUpdateRow(row.uuid, { creditObs: val }); }} />}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-24" field="currentExposure" title="Exposição Atual" width="130px" canSort align="right" filterType="range">
                {(row) => <span className="dark:text-gray-200">{fmtCurrency(row.currentExposure)}</span>}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-25" field="potentialExposure" title="Exposição Potencial" width="150px" canSort align="right" filterType="range">
                {(row) => <span className="dark:text-gray-200">{fmtCurrency(row.potentialExposure)}</span>}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-26" field="scrTotal" title="Total (R$ mil)" width="130px" canSort align="right" group="SCR" groupColor="green" filterType="range">
                {(row) => <span className="dark:text-gray-200">{fmtMilDiv(row.scrTotal)}</span>}
            </DataTableColumn>
            <DataTableColumn<CreditAnalysis> id="col-27" field="scrRural" title="Rural (R$ mil)" width="130px" canSort align="right" group="SCR" groupColor="green" filterType="range">
                {(row) => <span className="dark:text-gray-200">{fmtMilDiv(row.scrRural)}</span>}
            </DataTableColumn>
            <DataTableColumn<CreditAnalysis> id="col-28" field="overdue" title="Vencido (R$ mil)" width="120px" canSort align="right" group="SCR" groupColor="green" filterType="range">
                {(row) => <span className="dark:text-gray-200">{fmtMilDiv(row.overdue)}</span>}
            </DataTableColumn>
            <DataTableColumn<CreditAnalysis> id="col-28b" field="scrPrejuizo" title="Prejuízo (R$ mil)" width="130px" canSort align="right" group="SCR" groupColor="green" filterType="range">
                {(row) => <span className="dark:text-gray-200">{fmtMilDiv(row.scrPrejuizo)}</span>}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-29" field="score" title="Score Positivo" width="120px" canSort align="center" group="Serasa" filterType="range">
                {(row) => <span className="dark:text-gray-200">{row.score && parseFloat(row.score) !== 0 ? row.score : row.score === '0' || row.score === '0.0' ? 'N/C' : '-'}</span>}
            </DataTableColumn>
            <DataTableColumn<CreditAnalysis> id="col-30" field="pdef" title="Pdef Positivo" width="110px" canSort align="center" group="Serasa" filterType="range">
                {(row) => <span className="dark:text-gray-200">{row.score && parseFloat(row.score) === 0 ? 'N/C' : row.pdef ? `${parseFloat(row.pdef).toFixed(2)}%` : '-'}</span>}
            </DataTableColumn>
            <DataTableColumn<CreditAnalysis> id="col-29b" field="agroScore" title="AgroScore" width="110px" canSort align="center" group="Serasa" filterType="range">
                {(row) => {
                    const val = row.agroScore;
                    if (val && parseFloat(val) !== 0) return <span className="dark:text-gray-200">{val}</span>;
                    if (val && parseFloat(val) === 0) return <span className="dark:text-gray-200">N/C</span>;
                    if (!val && row.agroScoreConsulted) return <span className="text-orange-600 dark:text-orange-400 text-xs">Score Bloqueado</span>;
                    return <span className="dark:text-gray-200">-</span>;
                }}
            </DataTableColumn>
            <DataTableColumn<CreditAnalysis> id="col-30b" field="pdefAgro" title="Pdef Agro" width="110px" canSort align="center" group="Serasa" filterType="range">
                {(row) => <span className="dark:text-gray-200">{row.agroScore && parseFloat(row.agroScore) === 0 ? 'N/C' : row.pdefAgro ? `${parseFloat(row.pdefAgro).toFixed(2)}%` : '-'}</span>}
            </DataTableColumn>
            <DataTableColumn<CreditAnalysis> id="col-32" field="restriction" title="Restrições" width="200px" canSort group="Serasa">
                {(row) => {
                    const r = (row as any).restrictions || [];
                    if (!r.length) return <span className="text-gray-400">-</span>;
                    return <div className="text-xs leading-tight">{r.map((item: any, i: number) => <div key={i} className="text-red-700 dark:text-red-400">{item.type}: {item.count}x ({fmtCurrency(item.balance)})</div>)}</div>;
                }}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-31" field="scoreQuod" title="Score Quod" width="110px" canSort align="center" group="Quod" groupColor="purple" filterType="range" />
            <DataTableColumn<CreditAnalysis> id="col-31b" field="scoreBoaVista" title="Score BoaVista" width="120px" canSort align="center" group="BoaVista" groupColor="cyan" filterType="range" />

            <DataTableColumn<CreditAnalysis> id="col-ccir-ha" field="ccirHa" title="CCIR ha" width="100px" canSort align="right" group="CCIR" groupColor="amber" filterType="range">
                {(row) => { const n = parseFloat((row as any).ccirHa); return <span className="dark:text-gray-200">{!isNaN(n) ? n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</span>; }}
            </DataTableColumn>
            <DataTableColumn<CreditAnalysis> id="col-ccir-val" field="ccirValue" title="CCIR R$ (mil)" width="120px" canSort align="right" group="CCIR" groupColor="amber" filterType="range">
                {(row) => <span className="dark:text-gray-200">{(row as any).ccirValue ? fmtMil((row as any).ccirValue) : '-'}</span>}
            </DataTableColumn>
            <DataTableColumn<CreditAnalysis> id="col-patrim-scr" field="patrimonioScr" title="Patrim/SCR %" width="110px" canSort align="right" group="CCIR" groupColor="amber" filterType="range">
                {(row) => <span className="dark:text-gray-200">{(row as any).patrimonioScr ? `${(row as any).patrimonioScr}%` : '-'}</span>}
            </DataTableColumn>

            <DataTableColumn<CreditAnalysis> id="col-33" field="cprNumber" title="Nº do Contrato" width="120px" canSort>
                {(row) => <EditableTextField value={row.cprNumber} maxLength={20} onSave={async (val) => { await onSaveDecision(row.id, row.uuid, { status: row.status || 'PENDING', cprNumber: val }); onUpdateRow(row.uuid, { cprNumber: val }); }} />}
            </DataTableColumn>
        </>
    );
}
